
import { Component, Mixins, Prop, Watch, Emit } from "vue-property-decorator";
import { Point, Size, Vector, instanceOfIRectangleCoordinatable } from "../../../../../../../../base/domain/drawing";
import { ModelEditableMixin } from "../../../../../../../../base/components/system/mixins";
import { ObjectUtility, UuidFactory } from "../../../../../../../../base/utilities";
import { IQuestionnaire } from "../../../../../../../../documaker/domain/format/objects";
import { IConfiguration, NewObjectKeys } from "../../../../../../../domain/configuration";
import { FormatPageStructureEditor, IFormatObjectEditable, IEditableFormatObject } from "../../../../../../../domain/editableFormat";
import { instanceOfIVectorCoordinateEditable, instanceOfIRectangleCoordinateEditable, instanceOfIQuestionnairePropertyEditable } from "../../../../../../../domain/editableFormat/properties";
import { FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin, QuestionnaireGuidAssignableMixin } from "../../../../mixins";

@Component
export default class NewObjectLocatableMixin extends Mixins(ModelEditableMixin, FormatPageStructureEditableMixin, CommonComponentsAccessibleMixin, QuestionnaireGuidAssignableMixin) {
  writePropertyWhenValid: (writer: (source: FormatPageStructureEditor) => void) => void;
  readPropertyOrDefault: <T>(reader: (source: FormatPageStructureEditor) => T, defaultValue: T) => T;

  @Prop()
  locatorKey: NewObjectKeys;

  @Prop()
  isShown: boolean;

  @Prop({ type: Function, default: null })
  toLocateObjects: (source: IFormatObjectEditable) => Promise<IFormatObjectEditable[]>;

  @Emit()
  located(): void {}

  $configuration!: IConfiguration;

  data() {
    return {
      editor: null as IFormatObjectEditable,
      process: null as () => void,
      isProcessing: false,
    };
  }
  editor: IFormatObjectEditable;
  process: () => void;
  isProcessing: boolean;

  created(): void {
    this.initializeEditor();
  }

  initializeEditor(isForcibly: boolean = false): void {
    if (!isForcibly && (!this.isSourceEnable || this.editor)) {
      return;
    }
    this.editor = this.createNewObject(this.locatorKey);
  }

  createNewObject(source: NewObjectKeys | IEditableFormatObject): IFormatObjectEditable {
    if (!source) {
      return null;
    }
    if (instanceOfIRectangleCoordinatable(source)) {
      source.location.x = 0;
      source.location.y = 0;
    }
    return this.readPropertyOrDefault(s => s.createNewObject(source), null);
  }

  @Watch("locatorKey")
  onLocatorKeyChanged(newValue: NewObjectKeys): void {
    this.initializeEditor(true);
  }

  @Watch("editingSource")
  onEditingSourceChanged(): void {
    this.initializeEditor();
  }

  @Watch("process")
  onProcessUpdated(newValue: () => void, oldValue: () => void): void {
    this.reprocess();
  }

  @Watch("isShown")
  onIsShownUpdated(newValue: boolean, oldValue: boolean) {
    if (newValue) {
      if (this.process) {
        this.process();
      }
    } else {
      this.cancel();
    }
  }

  @Watch("editor")
  onEditorChanged(newValue: IFormatObjectEditable): void {
    if (!this.isProcessing) {
      this.reprocess();
    }
  }

  reprocess(): void {
    if (this.isProcessing) {
      this.cancel();
    }
    if (this.process) {
      this.process();
    }
  }

  locate(): void {
    this.processRequest(() => {
      this.getCommonComponents().getOperators().getCurrentPage().getLocator().locateObject(this, this.editor)
        .then(async r => {
          if (!r || !r.result) {
            return;
          }
          if (!instanceOfIRectangleCoordinateEditable(this.editor)) {
            return;
          }
          this.editor.location().setValue(Point.to(r.result));
          await this.addLocatedObject(r.index, r.isHitObject, r.isNextToObject);
          this.isProcessing = false;
          this.locate();
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  locateVertically(): void {
    this.processRequest(() => {
      this.getCommonComponents().getOperators().getCurrentPage().getLocator().locateObjectVertically(this, this.editor)
        .then(async r => {
          if (!r || !r.result) {
            return;
          }
          if (!instanceOfIRectangleCoordinateEditable(this.editor)) {
            return;
          }
          this.editor.location().setValue(Point.to(r.result));
          await this.addLocatedObject(r.index, r.isHitObject, r.isNextToObject);
          this.isProcessing = false;
          this.locateVertically();
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  layoutRectangle(minWidth: number = 0, minHeight: number = 0, fixedAspectRatio: number = 0): void {
    this.processRequest(() => {
      this.getCommonComponents().getOperators().getCurrentPage().getLocator().layoutRectangleObject(this, this.editor, fixedAspectRatio)
        .then(async r => {
          if (!r || !r.result) {
            return;
          }
          if (!instanceOfIRectangleCoordinateEditable(this.editor)) {
            return;
          }
          if (r.result.size.width < minWidth) { r.result.size.width = minWidth; }
          if (r.result.size.height < minHeight) { r.result.size.height = minHeight; }
          this.editor.location().setValue(Point.to(r.result.topLeft));
          this.editor.size().setValue(Size.to(r.result.size));
          await this.addLocatedObject(r.index, r.isHitObject, r.isNextToObject);
          this.isProcessing = false;
          this.layoutRectangle(minWidth, minHeight, fixedAspectRatio);
        })
        .catch(e => this.$error(
          "オブジェクト追加失敗",
          "オブジェクト追加操作中にエラーが発生しました.",
          e
        ));
    });
  }

  layoutLinear(): void {
    this.processRequest(() => {
      this.getCommonComponents().getOperators().getCurrentPage().getLocator().layoutLinearObject(this, this.editor)
      .then(async r => {
        if (!r || !r.result) {
          return;
        }
        if (!instanceOfIVectorCoordinateEditable(this.editor)) {
          return;
        }
        this.editor.vector().setValue(Vector.to(r.result));
        await this.addLocatedObject(r.index, r.isHitObject, r.isNextToObject);
        this.isProcessing = false;
        this.layoutLinear();
      })
      .catch(e => this.$error(
        "オブジェクト追加失敗",
        "オブジェクト追加操作中にエラーが発生しました.",
        e
      ));
    });
  }

  layoutPoints(): void {
    // TODO: Points用実装.NewObjectLocatableMixinを参考に.今回は不要？
  }

  private processRequest(callback: () => void): void {
    if (!this.isShown) {
      return; // 既に非表示になってる場合は処理しない.
    }
    if (!this.editingSource) {
      throw new Error("ソースが設定されていません.実装を確認してください.");
    }
    this.isProcessing = true;
    callback(); // tickで待つのはLocator側に任せる.
  }

  cancel(): void {
    this.getCommonComponents().getOperators().getCurrentPage().getLocator().cancel(this);
    this.isProcessing = false;
  }

  private async addLocatedObject(index?: number, isHitObject?: boolean, isNextObject?: boolean): Promise<void> {
    if (this.toLocateObjects) {
      const objects = await this.toLocateObjects(this.editor);
      if (objects.length === 0) {
        if (instanceOfIRectangleCoordinateEditable(this.editor)) {
          this.editor.location().setValue(new Point());
          this.editor.size().setValue(new Size(20, 20));
        }
        return;
      }
      this.writePropertyWhenValid(s => {
        const complementedIndex = index !== null && index !== undefined ? index : s.getObjects().length;
        for (let i = 0; i < objects.length; i++) {
          const object = objects[i];
          if (this.$configuration.formatEditor.extensions.questionnaire.isEnable && instanceOfIQuestionnairePropertyEditable(this.editor) && (0 < this.editor.selectableQuestionnaireTypes().length)) {
            // Questionnaire拡張時は_guidを採番する.
            const updated: IQuestionnaire = { ...this.editor.questionnaire().getValue() };
            updated._guid = UuidFactory.create();
            this.editor.questionnaire().setValue(updated);
          }
          this.assignNewGuidForQuestionnaire(object); // Questionnaire拡張使用時GUIDをアサインする.
          s.addObject(object, complementedIndex + i);
        }
        s.deselectAllObjects();
        const allObjects = s.getObjects();
        s.selectObjects(allObjects[complementedIndex]);
      });
    } else {
      if (this.editor.id().isEditable()) {
        this.editor.id().setValue(this.getCommonComponents().getPublisher().publish());
      }
      const guid = this.assignNewGuidForQuestionnaire(this.editor); // Questionnaire拡張使用時GUIDをアサインする.
      this.writePropertyWhenValid(s => {
        const addedIndex = this.addQuestionnaireChildGuids(s, index, isHitObject, isNextObject, [guid]);
        s.addObject(this.editor, addedIndex);
      });
    }
    this.editor = this.createNewObject(ObjectUtility.copyDeeply(this.editor.getSource()));
    this.located();
  }

  addLocatedObjects(objects: IFormatObjectEditable[]): void {
    this.writePropertyWhenValid(s => {
      for (const object of objects) {
        if (object.id().isEditable()) {
          object.id().setValue(this.getCommonComponents().getPublisher().publish());
        }
        this.assignNewGuidForQuestionnaire(object); // Questionnaire拡張使用時GUIDをアサインする.
        s.addObject(object);
      }
    });
    this.located();
  }
}
